/**
 * Ops-level aggregate recompute matrix (admin API).
 * Backs the Aggregates tab in System Health.
 *
 * GET returns every (assessment period × aggregate type) cell, with the cells
 * the version registry recommends flagged per row. POST recomputes exactly the
 * cells submitted — recommended or hand-picked.
 */
import { emptySplitApi } from '../../../../store/emptyApi';

export interface AggregateColumn {
  aggregate_type: number;
  name: string;
}

export interface AggregateMatrixRow {
  assessment_period_id: number;
  organization_id: number;
  organization_name: string;
  assessment_period_name: string;
  /** Aggregate types recommended for recompute — the default checked state. */
  recommended: number[];
  /** Aggregate types that already have a stored result. */
  present: number[];
}

export interface AggregateHealthResponse {
  columns: AggregateColumn[];
  rows: AggregateMatrixRow[];
  counts: { major: number; minor: number; minor_out_of_scope: number };
}

export interface RecomputeSelection {
  assessment_period_id: number;
  aggregate_type: number;
}

export interface ReconcileResponse {
  success: boolean;
  task_id?: string;
  dry_run?: boolean;
  scheduled?: RecomputeSelection[];
  message?: string;
}

export const AGGREGATE_HEALTH_TAG = 'AggregateHealthOps' as const;

const apiWithTag = emptySplitApi.enhanceEndpoints({
  addTagTypes: [AGGREGATE_HEALTH_TAG],
});

const aggregateHealthApi = apiWithTag.injectEndpoints({
  endpoints: build => ({
    adminAggregateHealth: build.query<AggregateHealthResponse, void>({
      query: () => ({ url: '/api/admin/v1/aggregate-health/' }),
      providesTags: [AGGREGATE_HEALTH_TAG],
    }),
    /** Omit `selection` to run everything recommended. */
    adminAggregateReconcile: build.mutation<
      ReconcileResponse,
      { selection?: RecomputeSelection[] }
    >({
      query: ({ selection }) => ({
        url: '/api/admin/v1/aggregate-health/',
        method: 'POST',
        body: selection ? { selection } : {},
      }),
      invalidatesTags: [AGGREGATE_HEALTH_TAG],
    }),
  }),
  overrideExisting: false,
});

export const { useAdminAggregateHealthQuery, useAdminAggregateReconcileMutation } =
  aggregateHealthApi;
export default aggregateHealthApi;
